import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coffee, HandHelping } from 'lucide-react';
import * as api from '@/api/supervision.api';
import type { MotivoDePausa } from '@/api/supervision.api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * PAUSAS DE LA ASESORA — reunión del 12-oct. El almuerzo no tiene hora fija y a veces hay
 * clientes en la tienda: la pausa la pone ella, cuando la necesita. Mientras dura no le caen
 * chats nuevos y, si lo pide, sus compañeras cubren los suyos.
 */

const MOTIVOS: Array<{ id: MotivoDePausa; etiqueta: string }> = [
  { id: 'almuerzo', etiqueta: 'Almuerzo' },
  { id: 'tienda', etiqueta: 'Atendiendo en la tienda' },
  { id: 'otra', etiqueta: 'Otra pausa' },
];

function minutosDesde(iso: string) {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
}

function usePausas() {
  return useQuery({ queryKey: ['pausas'], queryFn: api.pausas, refetchInterval: 30_000 });
}

/** En la cabecera, solo para quien atiende chats. */
export function BotonDePausa() {
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = usePausas();
  const [abierto, setAbierto] = useState(false);
  const [apoyo, setApoyo] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['pausas'] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
  };
  const empezar = useMutation({
    mutationFn: (motivo: MotivoDePausa) => api.empezarPausa(motivo, apoyo),
    onSuccess: () => {
      setAbierto(false);
      toast.success('En pausa. No te van a caer chats nuevos hasta que vuelvas.');
      refrescar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });
  const volver = useMutation({
    mutationFn: () => api.terminarPausa(),
    onSuccess: () => {
      toast.success('De vuelta: ya recibes chats.');
      refrescar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  if (user?.role !== 'AGENT') return null;
  const mia = data?.mia;

  if (mia) {
    return (
      <button
        type="button"
        onClick={() => volver.mutate()}
        disabled={volver.isPending}
        className="flex items-center gap-2 rounded-full bg-sky-100 px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-200"
        title="Terminar la pausa y volver a recibir chats"
        aria-label={`${mia.etiqueta}: volver`}
      >
        <Coffee className="h-4 w-4" />
        <span className="hidden sm:inline">
          {mia.etiqueta} · {minutosDesde(mia.desde)} min ·
        </span>
        <span className="underline">Volver</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="mac-capsula px-3 text-sm"
        aria-expanded={abierto}
        aria-label="Pausa"
      >
        <Coffee className="h-4 w-4" strokeWidth={1.75} />
        <span className="hidden sm:inline">Pausa</span>
      </button>
      {abierto && (
        <div className="mac-menu absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1.5rem)]">
          {MOTIVOS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => empezar.mutate(m.id)}
              disabled={empezar.isPending}
              className="mac-menu-fila"
            >
              {m.etiqueta}
            </button>
          ))}
          <label className="flex items-start gap-2 border-t border-surface-border px-3 py-2.5 text-xs text-slate-500">
            <input type="checkbox" className="mt-0.5" checked={apoyo} onChange={(e) => setApoyo(e.target.checked)} />
            Que mis compañeras vean y contesten mis chats mientras tanto
          </label>
        </div>
      )}
    </div>
  );
}

/** Debajo de la cabecera, a la asesora: a quién está cubriendo ahora. */
export function AvisoDeApoyo() {
  const { user } = useAuth();
  const { data } = usePausas();
  if (user?.role !== 'AGENT' || !data) return null;
  const cubro = data.abiertas.filter((p) => p.userId !== user.id && p.apoyo);
  if (cubro.length === 0) return null;
  return (
    <div className="mx-4 mt-2 flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-800 md:mx-6 dark:bg-sky-900/20 dark:text-sky-200">
      <HandHelping className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        Estás cubriendo a {cubro.map((p) => `${p.nombre} (${p.etiqueta.toLowerCase()})`).join(', ')}: sus chats salen en tu
        lista. Lo que contestes queda con tu nombre y el cliente sigue siendo de ella.
      </span>
    </div>
  );
}
